import { inject, Injectable } from '@angular/core';
import { Auth } from 'firebase/auth';
import {
  FirebaseStorage,
  getBlob,
  getDownloadURL,
  ref,
  StorageReference,
  uploadBytesResumable,
} from 'firebase/storage';
import { AssetFile } from '@verify/models';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import pLimit from 'p-limit';
import { BehaviorSubject, from, Observable, of, startWith, tap } from 'rxjs';
import { ChangeNotifierService } from './change-notifier.service';
import { FIREBASE_AUTH, FIREBASE_STORAGE } from './firebase.tokens';

const TRANSPARENT_PNG =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private storage: FirebaseStorage = inject(FIREBASE_STORAGE);
  private auth: Auth = inject(FIREBASE_AUTH);
  private changeNotifier = inject(ChangeNotifierService);

  private _uploads$ = new BehaviorSubject<
    Array<{ fileId: string; percentage: number }>
  >([]);
  private uploadLimiter = pLimit(3);
  readonly uploads$ = this._uploads$.pipe(this.changeNotifier.notifyOnEmit());

  constructor() {}

  uploadFile(
    filePath: string,
    file: Blob | Uint8Array | ArrayBuffer,
    fileId: string,
  ): Promise<string> {
    this._uploads$.next([...this._uploads$.value, { fileId, percentage: 0 }]);
    return this.changeNotifier.notifyWhenSettled(
      this.uploadLimiter(
        () =>
          new Promise<string>((resolve, reject) => {
            console.log('Uploading file', filePath);
            const storageRef = this.getStorageRef(filePath);
            const uploadTask = uploadBytesResumable(storageRef, file);
            uploadTask.on(
              'state_changed',
              (snapshot) => {
                this._uploads$.next(
                  this._uploads$.value.map((upload) =>
                    upload.fileId === fileId
                      ? {
                          fileId,
                          percentage:
                            (snapshot.bytesTransferred / snapshot.totalBytes) *
                            100,
                        }
                      : upload,
                  ),
                );
              },
              () => {
                this._uploads$.next(
                  this._uploads$.value.filter(
                    (upload) => upload.fileId !== fileId,
                  ),
                );
                reject(null);
              },
              () => {
                this._uploads$.next(
                  this._uploads$.value.filter(
                    (upload) => upload.fileId !== fileId,
                  ),
                );
                resolve(uploadTask.snapshot.ref.fullPath);
              },
            );
          }),
      ),
    );
  }

  getDownloadUrl(file: AssetFile): Observable<string> {
    const sessionItemName = `file:${file.filePath}${file.creationDate && 'toMillis' in file.creationDate ? file.creationDate.toMillis() : ''}`;
    try {
      const url = sessionStorage.getItem(sessionItemName);
      if (url) {
        return of(url);
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (exception) {
      // do nothing
    }
    const storageRef = ref(this.storage, file.filePath);
    return from(getDownloadURL(storageRef)).pipe(
      tap((url) => {
        if (url.startsWith('https')) {
          sessionStorage.setItem(sessionItemName, url);
        }
      }),
      startWith(TRANSPARENT_PNG),
      this.changeNotifier.notifyOnEmit(),
    );
  }

  getDownloadBlob(file: AssetFile): Observable<Blob> {
    return from(getBlob(ref(this.storage, file.filePath)));
  }

  getDownloadUrlFromPath(filePath: string): Observable<string> {
    const storageRef = ref(this.storage, filePath);
    return from(getDownloadURL(storageRef)).pipe(
      this.changeNotifier.notifyOnEmit(),
    );
  }

  getFullPath(filePath: string): string {
    const storageRef = ref(this.storage, filePath);
    return `gs://${storageRef.bucket}/${storageRef.fullPath}`;
  }

  downloadMultiple(filePaths: string[], fullPath = false): Observable<boolean> {
    const zip = new JSZip();

    const downloads = filePaths.map((filePath) =>
      getBlob(
        fullPath ? ref(this.storage, filePath) : this.getStorageRef(filePath),
      ).then((blob) => ({
        fileName: filePath.split('/').pop(),
        data: blob,
      })),
    );

    return from(
      Promise.all(downloads).then((filesWithData) => {
        filesWithData.forEach((file) => {
          zip.file(file.fileName, file.data, { binary: true });
        });

        return zip.generateAsync({ type: 'blob' }).then((content) => {
          if (content) {
            saveAs(content);
            return true;
          } else {
            return false;
          }
        });
      }),
    ).pipe(this.changeNotifier.notifyOnEmit());
  }

  private getStorageRef(filePath: string): StorageReference {
    return ref(this.storage, `${this.auth.tenantId}/${filePath}`);
  }
}
